import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { chakra } from '@chakra-ui/react'
import {
  ErrorMessage,
  Input,
  Pressable,
} from '@ayble-health/dashboard-components'
import { Button } from 'components/ui/button'
import { Chip } from 'components/ui/chip'
import { AwardIcon, AybleMarkIcon, ChevronDown, CloseIcon, PencilIcon, FileIcon, ThumbDown, ThumbUp } from './icons'
import {
  SYMPTOMS_FEEDBACK_META,
  SYMPTOMS_FEEDBACK_ORDER,
  IMPLEMENTATION_FEEDBACK_META,
  IMPLEMENTATION_FEEDBACK_ORDER,
  APP_CHECK_IN_META,
  AI_INSIGHT_REMOVAL_REASONS,
  feedbackAnswerMeta,
  type AiInsightActivity,
  type AiInsightRemovalReason,
  type AppCheckInActivity,
  type CareBlockActivity,
  type CareTeamFeedbackActivity,
  type FeedbackValence,
  type InstructionsActivity,
  type ImplementationFeedback,
  type SymptomsFeedback,
} from '../../models/careBlockDetail'
import { useCareBlockDetail } from '../../api/careOSPrototype/useCareBlockDetail'
import { useSubmitCareBlockFeedback } from '../../api/careOSPrototype/useSubmitCareBlockFeedback'

/**
 * Care Block Detail drawer — the inner view behind every Care Guide card.
 *
 * Two jobs, split top/bottom: the scrolling body is the block's history as a
 * timeline — each version of its instructions, with the care team's feedback,
 * the member's own app check-ins, and Ayble AI entries drafted from visit notes
 * beneath it — and the fixed footer captures new patient-reported feedback.
 *
 * Uses its own panel shell rather than the shared `components/Drawer`. That
 * one freezes both its width and its off-screen offset at mount time (it reads
 * `window.innerWidth` into a spring and writes an imperative inline width),
 * which fights a drawer that has to sit at a stable half of the viewport. The
 * shell here is a plain transform slide — no spring, no persisted width — and
 * leaves the shared Drawer untouched for the surfaces already using it.
 *
 * Deliberately takes only ids and callbacks, no careOS-specific context, so it
 * can be lifted into the HC dashboard's block drawer later without untangling.
 */

interface Props {
  /** Block to show. `null` closes the drawer. */
  blockId: string | null
  /** Whose profile this is — the actor on every `app-check-in` entry. */
  memberName: string
  onClose: () => void
}

// ─── Feedback pill ────────────────────────────────────────────────────────
// careOS one-off, same rationale as LaneBlockCard's TextOutlineButton: no
// ui/Button size maps onto this, and `Tag` is a span so it can't be the
// interactive control. Unselected is the neutral "Standard" pill; selected
// takes the answer's valence color.

const PILL_SELECTED_BG: Record<FeedbackValence, string> = {
  positive: 'var(--seafoam)',
  negative: 'var(--plum)',
}

function FeedbackPill({
  label, selected, valence, onClick,
}: {
  label: string
  selected: boolean
  valence: FeedbackValence
  onClick: () => void
}) {
  return (
    <chakra.button
      type="button"
      aria-pressed={selected}
      onClick={onClick}
      className={selected ? undefined : 'hover-aqua-outline'}
      css={{ borderRadius: 'pill', fontFamily: 'body' }}
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        padding: '7px 14px',
        border: '1px solid',
        borderColor: selected ? PILL_SELECTED_BG[valence] : 'var(--cloud)',
        background: selected ? PILL_SELECTED_BG[valence] : 'var(--canvas)',
        color: selected ? '#fff' : 'var(--seafoam)',
        fontSize: 'var(--text-cos-btn-sb-size)',
        fontWeight: 'var(--text-cos-btn-sb-weight)',
        letterSpacing: '0.02em',
        cursor: 'pointer',
        transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
      }}
    >
      {label}
    </chakra.button>
  )
}

// ─── Small shared bits ────────────────────────────────────────────────────

function InitialAvatar({ name, size = 24 }: { name: string; size?: number }) {
  return (
    <span
      aria-hidden
      style={{
        flexShrink: 0,
        width: size, height: size, borderRadius: '50%',
        background: 'var(--mint)', color: 'var(--seafoam)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'var(--font-body)',
        fontSize: size < 24 ? 10 : 'var(--text-xs-b-size)', fontWeight: 'var(--text-xs-b-weight)',
      }}
    >
      {name.trim().charAt(0).toUpperCase()}
    </span>
  )
}

export const valenceChipVariant = (valence: FeedbackValence) =>
  valence === 'positive' ? ('mint' as const) : ('ballet' as const)

const feedbackTags = (entry: CareTeamFeedbackActivity) =>
  [entry.symptoms, entry.implementation]
    .filter((a): a is SymptomsFeedback | ImplementationFeedback => !!a)
    .map(feedbackAnswerMeta)

const bodyText = {
  margin: 0, fontFamily: 'var(--font-body)',
  fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)',
  color: 'var(--evergreen)',
} as const

// ─── Timeline ─────────────────────────────────────────────────────────────
// Every row is a 36px gutter on the timeline rail (an avatar, or a pencil
// marker for an instructions change) beside the content: a name + date row,
// then the entry's card. Matches the production feed, where the avatar sits
// outside the card.

const GUTTER = 36
const RING = '0 0 0 4px var(--canvas)' // knocks the rail out behind each marker

function TimelineRow({ marker, children, last }: { marker: ReactNode; children: ReactNode; last?: boolean }) {
  return (
    <div style={{
      position: 'relative',
      display: 'grid', gridTemplateColumns: `${GUTTER}px minmax(0, 1fr)`, columnGap: 14,
      marginBottom: last ? 0 : 22,
    }}>
      <div>{marker}</div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  )
}

function PersonMarker({ name }: { name: string }) {
  return (
    <span style={{ display: 'inline-flex', borderRadius: '50%', boxShadow: RING }}>
      <InitialAvatar name={name} size={GUTTER} />
    </span>
  )
}

function AiMarker() {
  return (
    <span aria-hidden style={{
      width: GUTTER, height: GUTTER, borderRadius: '50%', boxShadow: RING,
      background: 'var(--marigold)', color: 'var(--seafoam)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <AybleMarkIcon size={22} />
    </span>
  )
}

function InstructionsMarker() {
  return (
    <span aria-hidden style={{
      width: GUTTER, height: GUTTER, borderRadius: '50%', boxShadow: RING,
      background: 'var(--canvas)', border: '1.5px solid var(--seafoam)', color: 'var(--seafoam)',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
    }}>
      <PencilIcon size={14} />
    </span>
  )
}

/** Name row above a card: who, an optional qualifier or tag, and the date. */
function NameRow({ name, qualifier, tag, date }: { name: string; qualifier?: string; tag?: ReactNode; date: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minHeight: GUTTER,
      fontFamily: 'var(--font-body)',
    }}>
      <span style={{ fontSize: 'var(--text-sm-b-size)', fontWeight: 'var(--text-sm-b-weight)', color: 'var(--evergreen)' }}>
        {name}
      </span>
      {qualifier && (
        <span style={{ fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)' }}>{qualifier}</span>
      )}
      {tag}
      <span style={{ marginLeft: 'auto', fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)' }}>{date}</span>
    </div>
  )
}

function EntryCard({ background = 'var(--canvas)', children }: { background?: string; children: ReactNode }) {
  return (
    <div style={{
      marginTop: 6, padding: '12px 14px',
      display: 'flex', flexDirection: 'column', gap: 8,
      background, border: '1px solid var(--cloud)', borderRadius: 'var(--radius-card, 8px)',
    }}>
      {children}
    </div>
  )
}

function ChartNoteButton({ label }: { label: string }) {
  // Visual affordance only — there's nowhere to navigate to yet.
  return (
    <Button variant="outline" gap="6px" flexShrink={0}>
      <FileIcon />
      {label}
    </Button>
  )
}

// ─── Feed entries ─────────────────────────────────────────────────────────

function CareTeamEntry({ entry, last }: { entry: CareTeamFeedbackActivity; last?: boolean }) {
  const tags = feedbackTags(entry)
  return (
    <TimelineRow marker={<PersonMarker name={entry.author.name} />} last={last}>
      <NameRow name={entry.author.name} date={entry.date} />
      <EntryCard>
        {(tags.length > 0 || entry.hasSoapNote) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
              {tags.map(meta => (
                <Chip key={meta.formLabel} variant={valenceChipVariant(meta.valence)}>{meta.formLabel}</Chip>
              ))}
            </span>
            {entry.hasSoapNote && <ChartNoteButton label="SOAP Note" />}
          </div>
        )}
        {entry.note && <p style={bodyText}>{entry.note}</p>}
      </EntryCard>
    </TimelineRow>
  )
}

function AppCheckInEntry({ entry, memberName, last }: { entry: AppCheckInActivity; memberName: string; last?: boolean }) {
  const meta = APP_CHECK_IN_META[entry.response]
  return (
    <TimelineRow marker={<PersonMarker name={memberName} />} last={last}>
      <NameRow name={memberName} qualifier="via app" date={entry.date} />
      {/* Feather so the member's own voice never reads as the care team's. */}
      <EntryCard background="var(--feather)">
        <span><Chip variant={valenceChipVariant(meta.valence)}>{meta.label}</Chip></span>
      </EntryCard>
    </TimelineRow>
  )
}

// Thumbs follow the claims insights (PreVisitClaimsInsights): 24px round
// Pressables inline after the copy. Unlike there, they leave once used — a
// thumbs up accepts the draft, a thumbs down opens the removal reasons.
export function Thumbs({ onChange }: { onChange: (direction: 'up' | 'down') => void }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 6, verticalAlign: 'middle' }}>
      {(['up', 'down'] as const).map(direction => {
        const Icon = direction === 'up' ? ThumbUp : ThumbDown
        return (
          <Pressable
            key={direction}
            type="button"
            aria-label={direction === 'up' ? 'Accept' : 'Remove'}
            onClick={() => onChange(direction)}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            w="24px"
            h="24px"
            borderRadius="full"
            flexShrink={0}
            color="bluegrey"
            className="hover-bg-feather"
          >
            <Icon />
          </Pressable>
        )
      })}
    </span>
  )
}

/** Local review state for one Ayble AI entry. Prototype-only: not persisted. */
export interface AiReview {
  status: 'accepted' | 'removing' | 'removed'
  reason?: AiInsightRemovalReason
  note?: string
}

function AiInsightEntry({ entry, review, onReview, last }: {
  entry: AiInsightActivity
  review?: AiReview
  onReview: (next: AiReview | undefined) => void
  last?: boolean
}) {
  const accepted = entry.status === 'accepted' || review?.status === 'accepted'
  const removing = review?.status === 'removing'

  const onThumb = (direction: 'up' | 'down') =>
    onReview({ status: direction === 'up' ? 'accepted' : 'removing' })

  return (
    <TimelineRow marker={<AiMarker />} last={last}>
      <NameRow
        name="Ayble AI"
        qualifier={accepted ? `· Accepted${entry.acceptedBy ? ` by ${entry.acceptedBy.name}` : ''}` : undefined}
        tag={accepted ? undefined : (
          // Last resort: the package Chip has no yellow variant, so this is
          // `neutral` recoloured to Ayble yellow — see UPSTREAM_PROPOSALS.md.
          <Chip variant="neutral" bg="marigold" color="evergreen" startElement={<AybleMarkIcon size={12} />}>
            Draft · needs review
          </Chip>
        )}
        date={entry.date}
      />
      <div style={{
        marginTop: 6, overflow: 'hidden', borderRadius: 'var(--radius-card, 8px)',
        // Ayble yellow marks everything the model wrote, reviewed or not;
        // only the "needs review" tag goes away once someone accepts it.
        background: 'var(--cream)',
        border: '1.5px solid var(--marigold)',
      }}>
        <div style={{
          padding: '10px 14px', borderBottom: '1px solid var(--cloud)',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-xs-b-size)', fontWeight: 'var(--text-xs-b-weight)', color: 'var(--seafoam)',
        }}>
          {entry.visitLabel}
        </div>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', padding: '12px 14px' }}>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <p style={bodyText}>
              {entry.note}
              {!accepted && !removing && <Thumbs onChange={onThumb} />}
            </p>
            {entry.quote && (
              <p style={{ ...bodyText, fontStyle: 'italic', paddingLeft: 10, borderLeft: '2px solid var(--marigold)' }}>
                “{entry.quote}”
              </p>
            )}
          </div>
          <ChartNoteButton label={entry.source === 'soap-note' ? 'SOAP Note' : 'Transcript'} />
        </div>
        {removing && <RemovalPanel review={review} onChange={onReview} />}
      </div>
    </TimelineRow>
  )
}

/** Thumbs down: why the draft is wrong. Remove appears once a reason is picked. */
export function RemovalPanel({ review, onChange }: { review: AiReview; onChange: (next: AiReview | undefined) => void }) {
  return (
    <div style={{
      padding: '12px 14px 14px', borderTop: '1px solid var(--cloud)',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <span style={{
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-xs-b-size)', fontWeight: 'var(--text-xs-b-weight)', color: 'var(--seafoam)',
      }}>
        Why remove this?
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {AI_INSIGHT_REMOVAL_REASONS.map(reason => (
          <FeedbackPill
            key={reason.value}
            label={reason.label}
            valence="negative"
            selected={review.reason === reason.value}
            onClick={() => onChange({ ...review, reason: review.reason === reason.value ? undefined : reason.value })}
          />
        ))}
      </div>
      <Input
        value={review.note ?? ''}
        onChange={e => onChange({ ...review, note: e.target.value })}
        placeholder="Add a note (optional)"
        aria-label="Removal note"
        h="36px"
        px={4}
        borderRadius="full"
        fontSize="sm"
        bg="canvas"
      />
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="outline" onClick={() => onChange(undefined)}>Cancel</Button>
        {review.reason && (
          <Button variant="solid" onClick={() => onChange({ ...review, status: 'removed' })}>Remove</Button>
        )}
      </div>
    </div>
  )
}

// ─── Instruction periods ──────────────────────────────────────────────────
// The feed is cut into instruction periods. The current period's instructions
// live in the drawer header, so its entries lead the feed; each earlier period
// collapses into a box (instructions + a summary of its feedback) that opens
// back into the full timeline. Displayed newest first by reversing the
// array's oldest-first order; dates are never parsed or sorted.

type FeedbackActivity = Exclude<CareBlockActivity, InstructionsActivity>

interface InstructionPeriod {
  /** Absent only for feedback that predates any instructions entry. */
  instructions?: InstructionsActivity
  endDate?: string
  feedback: FeedbackActivity[]
}

function groupByInstructions(activity: CareBlockActivity[]): InstructionPeriod[] {
  const periods = activity.reduce<InstructionPeriod[]>((acc, entry) => {
    if (entry.kind === 'instructions') {
      acc.push({ instructions: entry, feedback: [] })
    } else {
      if (acc.length === 0) acc.push({ feedback: [] })
      acc[acc.length - 1].feedback.push(entry)
    }
    return acc
  }, [])
  // A period ends where the next one starts; the last runs to the present.
  periods.forEach((period, i) => {
    period.endDate = periods[i + 1]?.instructions?.date
  })
  return periods
    .map(period => ({ ...period, feedback: [...period.feedback].reverse() }))
    .reverse()
}

/**
 * "Instructions · start – end · Set by …" — shared by the drawer header (the
 * current period) and each past period in the feed. "Set by" rather than
 * "Edited by": the first version is the original assignment.
 */
function InstructionsLabel({ instructions, endDate, trailing }: {
  instructions: InstructionsActivity
  endDate?: string
  trailing?: ReactNode
}) {
  return (
    <span style={{
      display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 5, width: '100%',
      fontFamily: 'var(--font-body)',
      fontSize: 'var(--text-xs-b-size)', fontWeight: 'var(--text-xs-b-weight)',
      letterSpacing: '0.01em', color: 'var(--seafoam)',
    }}>
      Instructions
      <span style={{ fontWeight: 'normal', color: 'var(--blue-grey)' }}>
        · {instructions.date} – {endDate ?? 'Present'} ·
      </span>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        fontWeight: 'normal', color: 'var(--blue-grey)',
      }}>
        <InitialAvatar name={instructions.author.name} size={16} />
        Set by {instructions.author.name}
      </span>
      {trailing && <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>{trailing}</span>}
    </span>
  )
}

export function Chevron({ open }: { open: boolean }) {
  return (
    <span aria-hidden style={{
      display: 'inline-flex', color: 'var(--seafoam)',
      transform: open ? 'rotate(180deg)' : undefined, transition: 'transform 0.15s',
    }}>
      <ChevronDown />
    </span>
  )
}

/** Count plus the latest care team ratings — the closed box's summary line. */
function PeriodSummary({ feedback }: { feedback: FeedbackActivity[] }) {
  // Newest first, so the first care team entry with answers is the latest.
  const latest = feedback.find((e): e is CareTeamFeedbackActivity =>
    e.kind === 'care-team-feedback' && feedbackTags(e).length > 0)
  return (
    <span style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6,
      fontFamily: 'var(--font-body)', fontSize: 'var(--text-xs-size)', color: 'var(--blue-grey)',
    }}>
      {feedback.length} {feedback.length === 1 ? 'entry' : 'entries'}
      {latest && feedbackTags(latest).map(meta => (
        <Chip key={meta.formLabel} variant={valenceChipVariant(meta.valence)}>{meta.formLabel}</Chip>
      ))}
    </span>
  )
}

function ActivityFeed({ activity, memberName }: { activity: CareBlockActivity[]; memberName: string }) {
  const periods = useMemo(() => groupByInstructions(activity), [activity])
  const [openPeriods, setOpenPeriods] = useState<Record<string, boolean>>({})
  const [reviews, setReviews] = useState<Record<string, AiReview | undefined>>({})

  if (activity.length === 0) {
    return (
      <div style={{
        border: '1.5px dashed var(--cloud)', borderRadius: 'var(--radius-card, 8px)',
        padding: '32px 20px', textAlign: 'center', background: 'var(--canvas)',
      }}>
        <p style={{
          margin: 0, fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-sm-size)', color: 'var(--blue-grey)',
        }}>
          No activity yet
        </p>
        <p style={{
          margin: '4px 0 0', fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-xs-size)', color: 'var(--steel)',
        }}>
          Instructions, care team feedback, and member check-ins will show up here.
        </p>
      </div>
    )
  }

  const visible = (feedback: FeedbackActivity[]) =>
    feedback.filter(e => reviews[e.id]?.status !== 'removed')

  const renderEntry = (entry: FeedbackActivity, last: boolean) => {
    if (entry.kind === 'care-team-feedback') return <CareTeamEntry key={entry.id} entry={entry} last={last} />
    if (entry.kind === 'app-check-in') return <AppCheckInEntry key={entry.id} entry={entry} memberName={memberName} last={last} />
    return (
      <AiInsightEntry
        key={entry.id}
        entry={entry}
        review={reviews[entry.id]}
        onReview={next => setReviews(prev => ({ ...prev, [entry.id]: next }))}
        last={last}
      />
    )
  }

  // Rows flatten across periods so the rail runs unbroken top to bottom.
  const rows: ReactNode[] = []
  periods.forEach((period, periodIndex) => {
    const feedback = visible(period.feedback)
    const isLastPeriod = periodIndex === periods.length - 1
    const isPast = !!(period.instructions && period.endDate)

    if (!isPast) {
      feedback.forEach((entry, i) => rows.push(renderEntry(entry, isLastPeriod && i === feedback.length - 1)))
      return
    }

    const instructions = period.instructions as InstructionsActivity
    const open = !!openPeriods[instructions.id]
    const toggle = () => setOpenPeriods(prev => ({ ...prev, [instructions.id]: !prev[instructions.id] }))

    if (!open) {
      rows.push(
        <TimelineRow key={instructions.id} marker={<InstructionsMarker />} last={isLastPeriod}>
          <Pressable
            type="button"
            aria-expanded={false}
            onClick={toggle}
            display="flex"
            flexDirection="column"
            gap="4px"
            w="100%"
            textAlign="left"
            p="10px 14px 12px"
            bg="var(--bg-subtle)"
            borderWidth="1px"
            borderColor="cloud"
            borderRadius="card"
          >
            <InstructionsLabel instructions={instructions} endDate={period.endDate} trailing={<Chevron open={false} />} />
            <span style={bodyText}>“{instructions.instruction}”</span>
            <PeriodSummary feedback={feedback} />
          </Pressable>
        </TimelineRow>
      )
      return
    }

    rows.push(
      <TimelineRow key={instructions.id} marker={<InstructionsMarker />} last={isLastPeriod && feedback.length === 0}>
        <div style={{ minHeight: GUTTER, display: 'flex', alignItems: 'center' }}>
          <InstructionsLabel
            instructions={instructions}
            endDate={period.endDate}
            trailing={(
              <Pressable
                type="button"
                aria-expanded
                aria-label="Collapse"
                onClick={toggle}
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
                w="28px"
                h="28px"
                borderRadius="full"
                className="hover-bg-feather"
              >
                <Chevron open />
              </Pressable>
            )}
          />
        </div>
        <p style={{ ...bodyText, marginTop: 2 }}>“{instructions.instruction}”</p>
      </TimelineRow>
    )
    feedback.forEach((entry, i) => rows.push(renderEntry(entry, isLastPeriod && i === feedback.length - 1)))
  })

  return (
    <div style={{ position: 'relative' }}>
      {/* The rail, centred under the gutter; markers knock it out with a ring. */}
      <div aria-hidden style={{
        position: 'absolute', left: GUTTER / 2 - 1, top: GUTTER, bottom: GUTTER,
        width: 2, background: 'var(--cloud)',
      }} />
      {rows}
    </div>
  )
}

// ─── Feedback capture ─────────────────────────────────────────────────────

function FeedbackRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
      <span style={{
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--text-xs-b-size)', fontWeight: 'var(--text-xs-b-weight)',
        letterSpacing: '0.01em', color: 'var(--seafoam)',
      }}>
        {label}
      </span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>{children}</div>
    </div>
  )
}

// ─── Drawer ───────────────────────────────────────────────────────────────

export function CareBlockDrawer({ blockId, memberName, onClose }: Props) {
  // Retain the last opened block through the close animation, so the body
  // doesn't blank out mid-slide when `blockId` goes null.
  const [displayedId, setDisplayedId] = useState<string | null>(blockId)
  useEffect(() => {
    if (blockId) setDisplayedId(blockId)
  }, [blockId])

  const { block, isLoading } = useCareBlockDetail(displayedId)
  // Start of the current instructions period, for the header's date line.
  const currentInstructions = useMemo(
    () => [...(block?.activity ?? [])].reverse()
      .find((entry): entry is InstructionsActivity => entry.kind === 'instructions'),
    [block]
  )
  const submitFeedback = useSubmitCareBlockFeedback(displayedId)

  const [symptoms, setSymptoms] = useState<SymptomsFeedback | undefined>()
  const [implementation, setImplementation] = useState<ImplementationFeedback | undefined>()
  const [note, setNote] = useState('')

  const resetForm = () => {
    setSymptoms(undefined)
    setImplementation(undefined)
    setNote('')
  }

  // A different block is a different form — never carry a half-made selection
  // from one block onto the next.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(resetForm, [displayedId])

  // The CTA exists only once something is selected (spec: Default state has no
  // CTA at all). One selection in either row is enough; the note is optional.
  const hasSelection = !!symptoms || !!implementation

  // ─── Feed scroll anchoring ──────────────────────────────────────────────
  // The feed reads newest → oldest, so the newest entry is at the top and
  // that's where the view has to land — on open and after a submission.
  //
  // Instant, never animated: submitting unmounts the CTA, which resizes the
  // footer and therefore the feed, so a smooth scroll would be racing a reflow
  // that changes its own target.
  const feedRef = useRef<HTMLDivElement>(null)
  const activityCount = block?.activity.length ?? 0

  useLayoutEffect(() => {
    const toTop = () => {
      const el = feedRef.current
      if (el) el.scrollTo({ top: 0, behavior: 'auto' })
    }
    // Applied twice on purpose. The inline call handles the ordinary case; the
    // deferred one re-asserts after the browser has reflowed, since the feed's
    // height is still settling when this commit's layout effects run and the
    // inline scroll can otherwise land against stale metrics.
    toTop()
    const raf = requestAnimationFrame(toTop)
    return () => cancelAnimationFrame(raf)
    // Keyed on the block and its entry count only: a re-render for any other
    // reason must not yank someone who has scrolled up to read history.
  }, [displayedId, activityCount])

  const handleSubmit = () => {
    if (!hasSelection || submitFeedback.isLoading) return
    submitFeedback.mutate(
      { symptoms, implementation, note: note.trim() || undefined },
      { onSuccess: resetForm }
    )
  }

  const isOpen = !!blockId

  // Escape closes, matching every other dismissible surface in the app.
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-hidden={!isOpen}
      aria-label={block ? `${block.title} details` : 'Care block details'}
      style={{
        position: 'fixed', top: 0, right: 0, zIndex: 30,
        height: '100vh',
        // Half the viewport, clamped so it stays usable on a narrow window and
        // never swallows the profile on a wide one.
        width: '50%', minWidth: 496, maxWidth: '70%',
        background: 'var(--canvas)',
        borderLeft: '1px solid var(--cloud)',
        boxShadow: isOpen ? '-8px 0 24px rgba(4, 51, 56, 0.10)' : 'none',
        transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
        // Visibility flips only after the slide-out finishes, so the panel
        // animates away instead of vanishing, but leaves the a11y tree when closed.
        visibility: isOpen ? 'visible' : 'hidden',
        pointerEvents: isOpen ? 'auto' : 'none',
        transition: `transform 260ms cubic-bezier(0.4, 0, 0.2, 1), visibility 0s linear ${isOpen ? '0s' : '260ms'}`,
        display: 'flex', flexDirection: 'column',
      }}
    >
      <div style={{
        height: '100%', width: '100%', minHeight: 0,
        display: 'flex', flexDirection: 'column',
        background: 'var(--canvas)',
      }}>
        {/* ── Header ── */}
        <div style={{
          flexShrink: 0,
          padding: '20px 24px 16px',
          borderBottom: '1px solid var(--cloud)',
          display: 'flex', flexDirection: 'column', gap: 6,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <h2 style={{
              flex: 1, minWidth: 0, margin: 0,
              fontFamily: 'var(--font-heading)',
              fontSize: 'var(--text-h4-size)', lineHeight: 'var(--text-h4-lh)',
              fontWeight: 'var(--text-h4-weight)', color: 'var(--evergreen)',
            }}>
              {block?.title ?? (isLoading ? 'Loading…' : 'Care block')}
            </h2>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
              {/* Affordance only, like Archive. `solid` because the package
                  Button has no mint variant — see UPSTREAM_PROPOSALS.md. */}
              {block?.canMarkComplete && <Button variant="solid">Mark Complete</Button>}
              <Button variant="outline">Archive</Button>

              {typeof block?.engagementCount === 'number' && (
                <Chip
                  variant="mint"
                  startElement={<AwardIcon />}
                  aria-label={`${block.engagementCount} engagements`}
                >
                  {block.engagementCount}
                </Chip>
              )}

              <Pressable
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="hover-evergreen"
                w="26px"
                h="26px"
                borderRadius="full"
                borderWidth="1px"
                borderColor="cloud"
                bg="canvas"
                color="seafoam"
                display="inline-flex"
                alignItems="center"
                justifyContent="center"
              >
                <CloseIcon />
              </Pressable>
            </div>
          </div>

          {currentInstructions && (
            <div style={{ marginTop: 4 }}>
              <InstructionsLabel instructions={currentInstructions} />
            </div>
          )}
          {block && (
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-sm-size)', lineHeight: 'var(--text-sm-lh)',
                color: currentInstructions ? 'var(--evergreen)' : 'var(--blue-grey)',
              }}>
                {block.instruction}
              </span>
              {/* Editing instructions is its own feature — this is the
                  affordance only, and opens no new instructions period. */}
              <Pressable
                type="button"
                aria-label="Edit instructions"
                className="hover-evergreen"
                flexShrink={0}
                p={0}
                color="seafoam"
                display="inline-flex"
                alignItems="center"
              >
                <PencilIcon />
              </Pressable>
            </div>
          )}
        </div>

        {/* ── Activity feed ── */}
        {/* Content flows from the top: a short feed keeps its entries up here
            with whitespace below, and only a feed tall enough to overflow
            scrolls — the anchoring above keeps the newest entry in view at the
            top. No bottom-alignment. */}
        <div ref={feedRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px' }}>
          {isLoading && !block ? (
            <p style={{
              margin: 0, fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-sm-size)', color: 'var(--blue-grey)',
            }}>
              Loading…
            </p>
          ) : (
            // Keyed by block so open sections and AI reviews never carry over.
            <ActivityFeed key={displayedId ?? ''} activity={block?.activity ?? []} memberName={memberName} />
          )}
        </div>

        {/* ── Feedback capture ── */}
        <div style={{
          flexShrink: 0,
          borderTop: '1px solid var(--cloud)',
          padding: '16px 24px 20px',
          display: 'flex', flexDirection: 'column', gap: 14,
          background: 'var(--canvas)',
        }}>
          <FeedbackRow label="Symptoms Feedback">
            {SYMPTOMS_FEEDBACK_ORDER.map(answer => {
              const meta = SYMPTOMS_FEEDBACK_META[answer]
              return (
                <FeedbackPill
                  key={answer}
                  label={meta.formLabel}
                  valence={meta.valence}
                  selected={symptoms === answer}
                  // Re-clicking the selected pill clears the row.
                  onClick={() => setSymptoms(current => (current === answer ? undefined : answer))}
                />
              )
            })}
          </FeedbackRow>

          <FeedbackRow label="Implementation Feedback">
            {IMPLEMENTATION_FEEDBACK_ORDER.map(answer => {
              const meta = IMPLEMENTATION_FEEDBACK_META[answer]
              return (
                <FeedbackPill
                  key={answer}
                  label={meta.formLabel}
                  valence={meta.valence}
                  selected={implementation === answer}
                  onClick={() =>
                    setImplementation(current => (current === answer ? undefined : answer))
                  }
                />
              )
            })}
          </FeedbackRow>

          <Input
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Notes"
            aria-label="Notes"
            h="40px"
            px={4}
            borderRadius="full"
            fontSize="sm"
          />

          {hasSelection && (
            <Button
              variant="solid"
              onClick={handleSubmit}
              disabled={submitFeedback.isLoading}
              loading={submitFeedback.isLoading}
              w="100%"
            >
              Add member-reported feedback
            </Button>
          )}

          {submitFeedback.isError && (
            <ErrorMessage message="Couldn't add that feedback. Try again." />
          )}
        </div>
      </div>
    </div>
  )
}
